import React, { useState, useCallback } from "react";
import { Button, Row } from "@amzn/meridian";
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from "@amzn/meridian/data-table";
import { downloadCSV, getCsvColumnCount } from "src/utils/csvUtils";

interface CsvTableProps {
  csvPreview: string;
  csvData?: string;
  title?: string;
  maxWidth?: string;
  hideDownloadButton?: boolean;
}

const CsvTable = ({
  csvPreview,
  csvData,
  maxWidth = "900px",
  hideDownloadButton = false,
}: CsvTableProps) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const parseCSV = (csvString: string) => {
    if (!csvString) return null;

    const lines = csvString.trim().split("\n");
    const parseCSVLine = (line: string) => {
      const result = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const rows = lines.slice(1).map((line) => parseCSVLine(line));
    return { headers, rows };
  };

  const tableData = parseCSV(csvPreview);

  const sort = (direction: string, key: string) => {
    if (!tableData || !key) return tableData;

    const columnIndex = parseInt(key);
    const sortedRows = [...tableData.rows].sort((a, b) => {
      const aVal = a[columnIndex] || "";
      const bVal = b[columnIndex] || "";

      if (aVal < bVal) {
        return direction === "ascending" ? -1 : 1;
      }
      if (aVal > bVal) {
        return direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return { ...tableData, rows: sortedRows };
  };

  const onSort = useCallback(({ sortColumn, sortDirection }) => {
    setSortColumn(sortColumn);
    setSortDirection(sortDirection);
  }, []);

  const sortedData = sort(sortDirection, sortColumn);

  const handleDownloadCSV = () => {
    if (csvData) {
      downloadCSV(csvData);
    }
  };

  if (!sortedData) return null;

  return (
    <>
      <div
        style={{
          overflowX: "auto",
          overflowY: "auto",
          maxWidth: maxWidth,
          maxHeight: "500px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          scrollbarWidth: "thin",
        }}
      >
        <DataTable
          onSort={onSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection as any}
          {...(getCsvColumnCount(csvPreview) > 4
            ? {
                layout: "fixed",
                width: `${getCsvColumnCount(csvPreview) * 120}px`,
              }
            : {
                width: "100%",
              })}
          stickyHeaderRows
          showBorder={true}
          showStripes={true}
          showColumnDividers={true}
          showRowDividers={true}
        >
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              {sortedData.headers.map((header, i) => (
                <DataTableHeader
                  key={i}
                  sortColumn={i.toString()}
                  resizable={true}
                >
                  {header}
                </DataTableHeader>
              ))}
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {sortedData.rows.map((row, rowIndex) => (
              <DataTableRow key={rowIndex} highlightOnHover={true}>
                {row.map((cell, colIndex) => (
                  <DataTableCell key={colIndex}>{cell}</DataTableCell>
                ))}
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>
      </div>
      {!hideDownloadButton && (
        <Row spacingInset={"300 none none none"}>
          <Button onClick={handleDownloadCSV}>Download CSV</Button>
        </Row>
      )}
    </>
  );
};

export default CsvTable;
